/**
 * Runtime Environment Configuration
 *
 * Reads environment variables from window.__RUNTIME_CONFIG__ (injected at container startup)
 * Falls back to import.meta.env (build-time) if not available
 */

interface RuntimeConfig {
  // Authentication & SSO
  VITE_AUTH_URL?: string;
  VITE_LOGIN_URL?: string;
  VITE_OIDC_CLIENT_ID?: string;
  VITE_OIDC_REDIRECT_URI?: string;
  VITE_OIDC_SCOPE?: string;
  VITE_AUTH_SERVICE_URL?: string;

  // API Endpoints
  VITE_API_URL?: string;
  VITE_API_BASE_URL?: string;
  VITE_UNIFIED_GATEWAY_URL?: string;
  VITE_WS_URL?: string;
  VITE_MISSION_GENERATION_PROTOCOL?: string;
  VITE_PRAETOR_MISSION_GENERATION_WS_URL?: string;

  // Platform URLs
  VITE_APP_URL?: string;

  // Platform Zones
  VITE_EXECUTION_ZONE_URL?: string;
  VITE_COORDINATION_ZONE_URL?: string;
  VITE_GOVERNANCE_ZONE_URL?: string;
  VITE_DATA_ZONE_URL?: string;

  // Authorization (Ory Keto)
  VITE_ORY_KETO_READ_URL?: string;
  VITE_ORY_KETO_WRITE_URL?: string;

  // Intelligence surface (SP3): page-cluster flags + dev-only runtime override
  VITE_ENABLE_RUNTIME_INGESTION?: string;
  VITE_ENABLE_RETRIEVAL_TUNING?: string;
  VITE_ENABLE_EXTRACTION_CURATION?: string;
  VITE_INTELLIGENCE_RUNTIME_URL?: string;

  // SP2 cutover-safety: legacy-zone surfaces (taxonomy, overrides, annotations,
  // event-spine) are proxied to the Rust intelligence-zone. This flag is
  // INVERSE-polarity — default TRUE preserves today's behavior (tabs shown).
  // A host set to "false" via the ConfigMap at cutover HIDES those surfaces so
  // the UI degrades gracefully instead of hard-failing when the zone is gone.
  VITE_ENABLE_LEGACY_ZONE_SURFACES?: string;
  // One-time central-registry backfill page. Off unless set to "true"; slated
  // for removal once the migration is complete everywhere (Agion#1897).
  VITE_ENABLE_INTELLIGENCE_BACKFILL?: string;

  // Legacy Service Aliases
  VITE_REGISTRY_SERVICE_URL?: string;
  VITE_MISSION_CONTROL_URL?: string;
  VITE_AGENT_RUNTIME_URL?: string;
  VITE_GOVERNANCE_SERVICE_URL?: string;

  // Observability (LGTM Stack)
  VITE_GRAFANA_URL?: string;
  VITE_LOKI_URL?: string;
  VITE_TEMPO_URL?: string;
  VITE_MIMIR_URL?: string;

  // WebSocket & Realtime
  VITE_SOCKETIO_URL?: string;
  VITE_STREAMING_URL?: string;

  // External Services
  VITE_CREDENTIAL_VAULT_URL?: string;

  // Development & Testing
  VITE_MOCK_AUTH?: string;
  VITE_AUTH_DISABLED?: string;
  VITE_USE_MOCK_DATA?: string;

  // Environment
  VITE_NODE_ENV?: string;
  VITE_ENVIRONMENT?: string;
  VITE_APP_VERSION?: string;
  VITE_DEBUG_MODE?: string;

  // Capsule Review Pilot (Slice 4 — reviewed-finalize rollout flag contract)
  VITE_CAPSULE_REVIEW_PILOT_ENABLED?: string;
  VITE_CAPSULE_REVIEW_BASE_URL?: string;

  // Ingestion UI v2 (WS3 — simplified single "Add data" page + 6-row progress)
  // Gates the new IngestionAutoPage + CapsuleProgressView. When unset or
  // false the legacy 4-step CapsuleWizardPage stays mounted at capsules/new.
  // Rollback: flip this to "false" in the ConfigMap.
  VITE_INGESTION_V2_ENABLED?: string;

  // Mission gallery + planner surfaces. Default disabled; enable per
  // environment once template deployment and planner activation are verified.
  VITE_MISSION_GALLERY_ENABLED?: string;

  // Admin MCP assistant. Default is disabled; enable per environment only
  // when the agion-admin-mcp backend flag is also enabled.
  VITE_ADMIN_MCP_ENABLED?: string;
}

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig;
  }
}

/**
 * Get runtime environment variable with fallbacks
 */
export function getRuntimeEnv(key: keyof RuntimeConfig): string | undefined {
  // 1. Try runtime injection (window.__RUNTIME_CONFIG__)
  const runtimeValue = window.__RUNTIME_CONFIG__?.[key];
  if (runtimeValue && runtimeValue !== '' && runtimeValue !== `\${${key}}`) {
    return runtimeValue;
  }

  // 2. Fall back to build-time (import.meta.env)
  return import.meta.env[key] as string | undefined;
}

/**
 * Get runtime environment variable with default
 */
export function getRuntimeEnvOrDefault(
  key: keyof RuntimeConfig,
  defaultValue: string
): string {
  return getRuntimeEnv(key) || defaultValue;
}

/**
 * Get configuration value with proper dev/prod fallback logic
 *
 * Priority:
 * 1. Runtime config from Kubernetes ConfigMap (window.__RUNTIME_CONFIG__)
 * 2. Build-time environment variable (import.meta.env)
 * 3. Development default (only in dev mode)
 * 4. Production: undefined (must be configured)
 *
 * @param key - Environment variable key
 * @param devDefault - Default value for development mode only
 * @returns Configuration value or undefined if not configured in production
 */
export function getConfig(key: keyof RuntimeConfig, devDefault?: string): string | undefined {
  // Priority 1: Runtime config from Kubernetes ConfigMap
  const runtime = window.__RUNTIME_CONFIG__?.[key];
  if (runtime !== undefined && runtime !== '' && runtime !== `\${${key}}`) {
    return runtime;
  }

  // Priority 2: Build-time environment variable
  const buildTime = import.meta.env[key];
  if (buildTime !== undefined && buildTime !== '') {
    return String(buildTime);
  }

  // Priority 3: Development default (only in dev mode)
  if (import.meta.env.DEV && devDefault) {
    return devDefault;
  }

  // Production: No fallback - must be configured
  return undefined;
}

/**
 * Get configuration value with required check
 * Throws error if value is not configured (for production safety)
 *
 * @param key - Environment variable key
 * @param devDefault - Default value for development mode only
 * @returns Configuration value
 * @throws Error if not configured in production
 */
export function getRequiredConfig(key: keyof RuntimeConfig, devDefault?: string): string {
  const value = getConfig(key, devDefault);

  if (value === undefined) {
    throw new Error(
      `CONFIGURATION ERROR: ${key} is required but not configured. ` +
      `Please set this value in your Kubernetes ConfigMap or environment variables.`
    );
  }

  return value;
}
